import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { CalendarX } from "lucide-react";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "En attente", color: "#999" },
  confirmed: { label: "Confirmé", color: "#0a0a0a" },
  cancelled: { label: "Annulé", color: "#c0392b" },
  completed: { label: "Terminé", color: "#666" },
};

type Booking = {
  id: string; resourceType: "power" | "venue"; label: string; booking_date: string; start_time: string; end_time: string;
  status: string; message?: string; price?: number | string | null;
  providerName?: string; providerAvatar?: string;
  requesterName?: string; requesterAvatar?: string;
  delivery_address?: string | null; on_behalf_of_name?: string | null; company_name?: string | null;
};

export default function Bookings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"client" | "provider">("client");
  const [asClient, setAsClient] = useState<Booking[]>([]);
  const [asProvider, setAsProvider] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/bookings/me").catch(() => ({ bookings: [] })),
      api.get("/bookings/provider").catch(() => ({ bookings: [] })),
      api.get("/venues/bookings/me").catch(() => ({ bookings: [] })),
      api.get("/venues/bookings/owner").catch(() => ({ bookings: [] })),
    ]).then(([mine, prov, venueMine, venueOwner]) => {
      const mapPower = (b: any): Booking => ({ ...b, resourceType: "power", label: b.power_label, providerName: b.provider_name, providerAvatar: b.provider_avatar, requesterName: b.requester_name, requesterAvatar: b.requester_avatar });
      const mapVenue = (b: any): Booking => ({ ...b, resourceType: "venue", label: b.venue_name, providerName: b.venue_name, providerAvatar: b.venue_avatar, requesterName: b.requester_name, requesterAvatar: b.requester_avatar });
      setAsClient([...(mine?.bookings || []).map(mapPower), ...(venueMine?.bookings || []).map(mapVenue)].sort((a, b) => b.booking_date.localeCompare(a.booking_date)));
      setAsProvider([...(prov?.bookings || []).map(mapPower), ...(venueOwner?.bookings || []).map(mapVenue)].sort((a, b) => b.booking_date.localeCompare(a.booking_date)));
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const confirm = (b: Booking) => {
    const path = b.resourceType === "venue" ? `/venues/bookings/${b.id}/confirm` : `/bookings/${b.id}/confirm`;
    api.put(path, {}).then(() => load()).catch(() => {});
  };
  const cancel = (b: Booking) => {
    if (!window.confirm("Annuler cette réservation ?")) return;
    const path = b.resourceType === "venue" ? `/venues/bookings/${b.id}/cancel` : `/bookings/${b.id}/cancel`;
    api.put(path, {}).then(() => load()).catch(() => {});
  };

  const list = tab === "client" ? asClient : asProvider;

  // Une réservation annulée ou terminée (ou simplement passée dans le temps, même si son statut
  // est resté "confirmed" faute de job de fond pour le faire évoluer) n'a plus rien à faire mêlée
  // aux réservations à venir — elle bascule en Archives, cochée séparément via le bouton ci-dessous
  // plutôt que noyée dans la même liste (ce que le fondateur signalait : "Annulé" apparaissait
  // directement au milieu des réservations actives).
  const [showArchived, setShowArchived] = useState(false);
  const isArchived = (b: Booking) => {
    if (b.status === "cancelled" || b.status === "completed") return true;
    return new Date(`${b.booking_date}T${b.end_time}`) < new Date();
  };
  const activeList = list.filter(b => !isArchived(b));
  const archivedList = list.filter(isArchived);
  const shownList = showArchived ? archivedList : activeList;

  // "Mes prestataires" — reprendre rendez-vous en un tap avec un lieu/prestataire déjà réservé,
  // dérivé de l'historique déjà chargé (asClient) plutôt qu'un nouvel endpoint : dédoublonné par
  // lieu (venue_id) ou par Power (provider_id), le plus récent en premier.
  const myProviders = React.useMemo(() => {
    const seen = new Set<string>();
    const out: { key: string; name: string; avatar?: string; onClick: () => void }[] = [];
    for (const b of asClient) {
      const raw = b as any;
      const key = b.resourceType === "venue" ? `venue-${raw.venue_id}` : `power-${raw.provider_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        key, name: b.providerName || "?", avatar: b.providerAvatar,
        onClick: () => navigate(b.resourceType === "venue" ? `/venue/${raw.venue_id}` : `/profile/${raw.provider_id}`),
      });
    }
    return out;
  }, [asClient]);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
          <span style={{ fontSize: 22, fontWeight: 700 }}>Mes réservations</span>
        </div>

        <div style={{ display: "flex", gap: 8, padding: "16px 24px 0" }}>
          <button onClick={() => { setTab("client"); setShowArchived(false); }} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "client" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "client" ? "#0a0a0a" : "white", color: tab === "client" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            En tant que client {asClient.length > 0 ? `(${asClient.length})` : ""}
          </button>
          <button onClick={() => { setTab("provider"); setShowArchived(false); }} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "provider" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "provider" ? "#0a0a0a" : "white", color: tab === "provider" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            En tant que prestataire {asProvider.length > 0 ? `(${asProvider.length})` : ""}
          </button>
          {archivedList.length > 0 && (
            <button onClick={() => setShowArchived(a => !a)} style={{ marginLeft: "auto", padding: "8px 16px", borderRadius: 20, border: showArchived ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: showArchived ? "#0a0a0a" : "white", color: showArchived ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Archives ({archivedList.length})
            </button>
          )}
        </div>

        {loading && <div style={{ textAlign: "center", padding: 60, color: "#999" }}>Chargement…</div>}

        {!loading && shownList.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <CalendarX size={40} strokeWidth={1.5} color="#ccc" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>{showArchived ? "Aucune réservation archivée" : "Aucune réservation"}</div>
          </div>
        )}

        {!showArchived && tab === "client" && myProviders.length > 0 && (
          <div style={{ padding: "16px 24px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#999", marginBottom: 10 }}>Mes prestataires</div>
            <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 4 }}>
              {myProviders.map(p => (
                <div key={p.key} onClick={p.onClick} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 62, flexShrink: 0, cursor: "pointer" }}>
                  <div style={{ width: 54, height: 54, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15, overflow: "hidden" }}>
                    {p.avatar ? <img src={p.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (p.name || "?")[0]}
                  </div>
                  <span style={{ fontSize: 11, color: "#999", marginTop: 4, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>{(p.name || "").split(" ")[0]}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && shownList.length > 0 && (
          <div style={{ padding: "16px 24px" }}>
            {shownList.map(b => {
              const person = tab === "client" ? { name: b.providerName, avatar: b.providerAvatar } : { name: b.requesterName, avatar: b.requesterAvatar };
              const status = STATUS_LABELS[b.status] || STATUS_LABELS.pending;
              return (
                <div key={`${b.resourceType}-${b.id}`} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 0", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, overflow: "hidden", flexShrink: 0 }}>
                    {person.avatar ? <img src={person.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(person.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      {b.label} {tab === "client" ? (b.resourceType === "venue" ? "" : "avec") : "pour"} {tab === "provider" || b.resourceType === "power" ? person.name : ""}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#666", marginTop: 2 }}>
                      {new Date(b.booking_date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {b.start_time.slice(0, 5)}–{b.end_time.slice(0, 5)}
                      {b.price != null && ` · ${Number(b.price).toFixed(2)} €`}
                    </div>
                    {b.message && <div style={{ fontSize: 12.5, color: "#999", marginTop: 4, fontStyle: "italic" }}>"{b.message}"</div>}
                    {(b.on_behalf_of_name || b.company_name) && (
                      <div style={{ fontSize: 12.5, color: "#999", marginTop: 4 }}>Pour {b.on_behalf_of_name || b.company_name}</div>
                    )}
                    {b.delivery_address && (
                      <div style={{ fontSize: 12.5, color: "#999", marginTop: 4 }}>Adresse : {b.delivery_address}</div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: status.color, backgroundColor: "#F5F5F5", borderRadius: 10, padding: "3px 9px" }}>{status.label}</span>
                      {/* !showArchived : une réservation passée mais restée au statut "confirmed"
                          (aucun job de fond ne la fait évoluer) atterrit dans Archives via
                          isArchived() ci-dessus — elle n'a plus rien à confirmer/annuler. */}
                      {!showArchived && tab === "provider" && b.status === "pending" && (
                        <button onClick={() => confirm(b)} style={{ fontSize: 12, fontWeight: 600, color: "#0a0a0a", background: "none", border: "none", cursor: "pointer" }}>Confirmer</button>
                      )}
                      {!showArchived && ["pending", "confirmed"].includes(b.status) && (
                        <button onClick={() => cancel(b)} style={{ fontSize: 12, fontWeight: 600, color: "#c0392b", background: "none", border: "none", cursor: "pointer" }}>Annuler</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
